import React, { useState } from 'react';
import { CalendarIcon, MapPinIcon, ClockIcon, UsersIcon, TagIcon, ChevronRightIcon, SendIcon, CheckIcon, XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageTransition } from '../components/layout/PageTransition';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input, TextArea } from '../components/ui/Input';
import { DecorativeShape } from '../components/DecorativeShape';
import { Animate } from '../components/ui/Animate';
import { useLanguage } from '../context/LanguageContext';

// ─────────────────────────────────────────────────────────────
// FORM EMAIL — same as Contact page
// ─────────────────────────────────────────────────────────────
const FORM_EMAIL = 'helgi@helgig.com';

// ─────────────────────────────────────────────────────────────
// EVENT DATA
// Update this list to add/remove/modify scheduled events.
//
// priceIS / priceEN: shown on the card, e.g. "49.900 kr." or "Contact for pricing"
// status: 'open' | 'few-left' | 'full' | 'coming-soon'
// ─────────────────────────────────────────────────────────────
interface ScheduledEvent {
  id: string;
  titleIS: string;
  titleEN: string;
  dateIS: string;
  dateEN: string;
  timeIS: string;
  timeEN: string;
  locationIS: string;
  locationEN: string;
  formatIS: string;
  formatEN: string;
  priceIS: string;
  priceEN: string;
  spotsTotal: number | null;
  spotsLeft: number | null;
  status: 'open' | 'few-left' | 'full' | 'coming-soon';
  internalPath: string;
  tagIS: string;
  tagEN: string;
  accentColor: string;
  accentText: string;
  accentLight: string;
}
const events: ScheduledEvent[] = [{
  id: 'ftm-autumn-2026',
  titleIS: 'Stjórnandi í Fyrsta Sinn',
  titleEN: 'First-Time Manager',
  dateIS: 'Haust 2026 - dagsetning í vinnslu',
  dateEN: 'Autumn 2026 - date to be confirmed',
  timeIS: '',
  timeEN: '',
  locationIS: 'Á netinu (Zoom)',
  locationEN: 'Online (Zoom)',
  formatIS: '6 vikur · Ein lota á viku',
  formatEN: '6 weeks · One session per week',
  priceIS: 'Hafðu samband',
  priceEN: 'Contact for pricing',
  spotsTotal: null,
  spotsLeft: null,
  status: 'coming-soon',
  internalPath: '/services/courses/first-time-manager',
  tagIS: 'Námskeið',
  tagEN: 'Course',
  accentColor: 'bg-coral',
  accentText: 'text-coral',
  accentLight: 'bg-coral/10'
}, {
  id: 'conflict-autumn-2026',
  titleIS: 'Conflict Coaching',
  titleEN: 'Conflict Coaching',
  dateIS: 'Haust 2026 - dagsetning í vinnslu',
  dateEN: 'Autumn 2026 - date to be confirmed',
  timeIS: '',
  timeEN: '',
  locationIS: 'Reykjavík (staðfest við skráningu)',
  locationEN: 'Reykjavík (venue confirmed on booking)',
  formatIS: '2 dagar · Millibil milli daga',
  formatEN: '2 days · Practice interval between',
  priceIS: 'Hafðu samband',
  priceEN: 'Contact for pricing',
  spotsTotal: null,
  spotsLeft: null,
  status: 'coming-soon',
  internalPath: '/services/courses/conflict-coaching',
  tagIS: 'Námskeið',
  tagEN: 'Course',
  accentColor: 'bg-teal',
  accentText: 'text-teal',
  accentLight: 'bg-teal/10'
}, {
  id: 'meetings-autumn-2026',
  titleIS: 'Betri Fjarfundir',
  titleEN: 'Better Online Meetings',
  dateIS: 'Haust 2026 - dagsetning í vinnslu',
  dateEN: 'Autumn 2026 - date to be confirmed',
  timeIS: '',
  timeEN: '',
  locationIS: 'Á netinu (Zoom)',
  locationEN: 'Online (Zoom)',
  formatIS: '2 x 2 klst',
  formatEN: '2 x 2 hrs',
  priceIS: 'Hafðu samband',
  priceEN: 'Contact for pricing',
  spotsTotal: null,
  spotsLeft: null,
  status: 'coming-soon',
  internalPath: '/services/courses/better-online-meetings',
  tagIS: 'Námskeið',
  tagEN: 'Course',
  accentColor: 'bg-blue-600',
  accentText: 'text-blue-600',
  accentLight: 'bg-blue-50'
}];

// ─────────────────────────────────────────────────────────────
// Registration modal / inline form
// ─────────────────────────────────────────────────────────────
interface RegFormProps {
  event: ScheduledEvent;
  isIS: boolean;
  onClose: () => void;
}
function RegistrationForm({
  event,
  isIS,
  onClose
}: RegFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const title = isIS ? event.titleIS : event.titleEN;
  const date = isIS ? event.dateIS : event.dateEN;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(p => ({
      ...p,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setHasError(false);
    try {
      const res = await fetch(`https://formsubmit.co/ajax/${FORM_EMAIL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message || '—',
          _subject: `Course registration interest: ${title}`,
          course: title,
          date
        })
      });
      if (res.ok) {
        setIsSubmitted(true);
      } else {
        setHasError(true);
      }
    } catch {
      setHasError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-id="element-981">
      <Animate data-id="element-982">
        <Card className="w-full max-w-md p-8 relative shadow-2xl" data-id="element-983">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close" data-id="element-984">
            <XIcon size={20} data-id="element-985" />
          </button>

          {isSubmitted ? <div className="text-center py-6" data-id="element-986">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-987">
                <CheckIcon size={28} strokeWidth={2.5} data-id="element-988" />
              </div>
              <h3 className="text-xl font-bold text-charcoal mb-2" data-id="element-989">
                {isIS ? 'Skráning móttekin!' : 'Got it!'}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-6" data-id="element-990">
                {isIS ? 'Ég kem til baka til þín þegar dagsetning er staðfest.' : "I'll be in touch as soon as the date is confirmed."}
              </p>
              <Button variant="outline" onClick={onClose} size="sm" data-id="element-991">
                {isIS ? 'Loka' : 'Close'}
              </Button>
            </div> : <>
              <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-4 ${event.accentLight} ${event.accentText}`} data-id="element-992">
                {isIS ? event.tagIS : event.tagEN}
              </div>
              <h3 className="text-xl font-bold text-charcoal mb-1" data-id="element-993">{title}</h3>
              <p className="text-sm text-gray-500 mb-6" data-id="element-994">
                {isIS ? 'Láttu mig vita og ég kem til baka þegar dagsetning er staðfest.' : 'Leave your details and I will contact you when the date is confirmed.'}
              </p>

              {hasError && <div className="p-3 bg-red-50 text-red-700 rounded-xl text-sm mb-4 border border-red-100" data-id="element-995">
                  {isIS ? 'Eitthvað fór úrskeiðis. Sendu beint á ' : 'Something went wrong. Email directly: '}
                  <a href={`mailto:${FORM_EMAIL}`} className="underline font-bold" data-id="element-996">{FORM_EMAIL}</a>
                </div>}

              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-997">
                <Input label={isIS ? 'Nafn' : 'Name'} name="name" placeholder={isIS ? 'Jón Jónsson' : 'Jane Smith'} value={formData.name} onChange={handleChange} required data-id="element-998" />
                <Input label={isIS ? 'Netfang' : 'Email'} name="email" type="email" placeholder={isIS ? 'jon@example.com' : 'jane@example.com'} value={formData.email} onChange={handleChange} required data-id="element-999" />
                <TextArea label={isIS ? 'Spurningar eða athugasemdir (valfrjálst)' : 'Questions or comments (optional)'} name="message" rows={3} placeholder={isIS ? 'Hér má setja spurningar eða sérstakar óskir...' : 'Any questions or special requirements...'} value={formData.message} onChange={handleChange} data-id="element-1000" />
                <Button type="submit" className="w-full justify-center" size="lg" isLoading={isSubmitting} rightIcon={<SendIcon size={16} data-id="element-1002" />} data-id="element-1001">
                  {isIS ? 'Láta vita' : 'Notify me'}
                </Button>
              </form>
            </>}
        </Card>
      </Animate>
    </div>;
}

// ─────────────────────────────────────────────────────────────
// Status badge
// ─────────────────────────────────────────────────────────────
function StatusBadge({
  event,
  isIS
}: {
  event: ScheduledEvent;
  isIS: boolean;
}) {
  const map = {
    open: {
      label: isIS ? 'Skráning opin' : 'Open',
      classes: 'bg-green-100 text-green-700'
    },
    'few-left': {
      label: isIS ? 'Fá pláss eftir' : 'Few spots left',
      classes: 'bg-amber-100 text-amber-700'
    },
    full: {
      label: isIS ? 'Fullskráð' : 'Full',
      classes: 'bg-red-100 text-red-700'
    },
    'coming-soon': {
      label: isIS ? 'Í vinnslu' : 'Coming soon',
      classes: 'bg-gray-100 text-gray-500'
    }
  };
  const s = map[event.status];
  return <span className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${s.classes}`} data-id="element-1003">
      {s.label}
    </span>;
}

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export function Events() {
  const {
    language
  } = useLanguage();
  const isIS = language === 'is';
  const [activeRegistration, setActiveRegistration] = useState<ScheduledEvent | null>(null);
  return <PageTransition data-id="element-1004">
      {/* Registration modal */}
      {activeRegistration && <RegistrationForm event={activeRegistration} isIS={isIS} onClose={() => setActiveRegistration(null)} data-id="element-1005" />}

      <div className="pt-12 pb-24" data-id="element-1006">
        {/* Header */}
        <section className="text-center max-w-4xl mx-auto px-4 mb-16 relative" data-id="element-1007">
          <DecorativeShape type="square" color="bg-coral/10" className="top-0 right-0 w-32 h-32 rotate-12 -z-10" data-id="element-1008" />
          <DecorativeShape type="circle" color="bg-teal/10" className="bottom-0 left-0 w-24 h-24 -z-10" data-id="element-1009" />
          <Animate data-id="element-1010">
            <span className="inline-block px-4 py-2 bg-teal/10 text-teal font-medium rounded-full text-sm mb-6" data-id="element-1011">
              {isIS ? 'Námskeið og vinnustofur' : 'Courses and workshops'}
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-4" data-id="element-1012">
              {isIS ? 'Næstu viðburðir' : 'Upcoming events'}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1013">
              {isIS ? 'Námskeið og vinnustofur eru í undirbúningi fyrir haust 2026. Skráðu þig á biðlista og ég kem til baka þegar dagsetningar eru staðfestar.' : 'Courses and workshops are being scheduled for autumn 2026. Register your interest below and I will be in touch when dates are confirmed.'}
            </p>
          </Animate>
        </section>

        {/* Event cards */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8" data-id="element-1014">
          {events.map((event, i) => <Animate key={event.id} delay={i * 0.08} data-id="element-1015">
              <Card className="p-0 overflow-hidden border-2 border-gray-100 transition-colors" data-id="element-1016">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-0" data-id="element-1017">
                  {/* Accent panel */}
                  <div className={`lg:col-span-3 ${event.accentLight} p-8 flex flex-col justify-center items-center text-center gap-3`} data-id="element-1018">
                    <div className={`w-14 h-14 rounded-full ${event.accentColor} text-white flex items-center justify-center shadow-md`} data-id="element-1019">
                      <CalendarIcon size={24} data-id="element-1020" />
                    </div>
                    <div data-id="element-1021">
                      <p className={`font-bold text-base ${event.accentText} leading-snug`} data-id="element-1022">
                        {isIS ? event.dateIS : event.dateEN}
                      </p>
                      {(isIS ? event.timeIS : event.timeEN) && <p className="text-xs text-gray-500 mt-1.5 flex items-center justify-center gap-1" data-id="element-1023">
                          <ClockIcon size={11} data-id="element-1024" />
                          {isIS ? event.timeIS : event.timeEN}
                        </p>}
                    </div>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${event.accentLight} ${event.accentText}`} data-id="element-1025">
                      {isIS ? event.tagIS : event.tagEN}
                    </span>
                  </div>

                  {/* Content panel */}
                  <div className="lg:col-span-9 p-8 bg-white flex flex-col justify-between gap-5" data-id="element-1026">
                    <div data-id="element-1027">
                      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap" data-id="element-1028">
                        <h2 className="text-2xl font-bold text-charcoal" data-id="element-1029">
                          {isIS ? event.titleIS : event.titleEN}
                        </h2>
                        <StatusBadge event={event} isIS={isIS} data-id="element-1030" />
                      </div>

                      <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-500" data-id="element-1031">
                        <span className="flex items-center gap-1.5" data-id="element-1032">
                          <MapPinIcon size={13} className="flex-shrink-0" data-id="element-1033" />
                          {isIS ? event.locationIS : event.locationEN}
                        </span>
                        <span className="flex items-center gap-1.5" data-id="element-1034">
                          <TagIcon size={13} className="flex-shrink-0" data-id="element-1035" />
                          {isIS ? event.formatIS : event.formatEN}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-5 border-t border-gray-100" data-id="element-1036">
                      <button onClick={() => setActiveRegistration(event)} className="inline-flex" data-id="element-1037">
                        <Button variant="outline" rightIcon={<ChevronRightIcon size={15} data-id="element-1039" />} data-id="element-1038">
                          {isIS ? 'Láta vita þegar skráning opnar' : 'Notify me when registration opens'}
                        </Button>
                      </button>

                      <Link to={event.internalPath} data-id="element-1040">
                        <Button variant="outline" size="sm" data-id="element-1041">
                          {isIS ? 'Um námskeiðið' : 'About the course'}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </Card>
            </Animate>)}
        </section>

        {/* Payment note */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" data-id="element-1042">
          <Animate data-id="element-1043">
            <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-2xl p-5" data-id="element-1044">
              <div className="w-5 h-5 rounded-full bg-gray-300 text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold" data-id="element-1045">i</div>
              <p className="text-sm text-gray-600" data-id="element-1046">
                {isIS ? 'Við staðfestingu dagsetningar fær þú tölvupóst með skráningarleiðbeiningum og greiðsluupplýsingum. Greiðsla fer fram með reikningi eða bankamillifærslu. Við erfiðleika, hafðu samband beint á ' : 'When dates are confirmed you will receive an email with registration instructions and payment details. Payment is by invoice or bank transfer. For any questions, contact me directly at '}
                <a href={`mailto:${FORM_EMAIL}`} className="text-teal font-medium hover:underline" data-id="element-1047">{FORM_EMAIL}</a>
              </p>
            </div>
          </Animate>
        </section>

        {/* Custom / in-house CTA */}
        <section className="max-w-4xl mx-auto px-4 mt-16" data-id="element-1048">
          <Animate data-id="element-1049">
            <Card className="p-10 bg-gradient-to-br from-teal/5 to-coral/5 border-0 text-center" data-id="element-1050">
              <h2 className="text-2xl font-bold mb-3" data-id="element-1051">
                {isIS ? 'Ertu að leita að sérsniðinni þjálfun?' : 'Looking for something tailored?'}
              </h2>
              <p className="text-gray-600 mb-6 max-w-lg mx-auto" data-id="element-1052">
                {isIS ? 'Ég hanna og held námskeið og vinnustofur innanhúss hjá fyrirtækjum - aðlagað að ykkar samhengi og þörfum.' : 'I design and run in-house courses and workshops for organisations - adapted to your context and needs.'}
              </p>
              <Link to="/contact" data-id="element-1053">
                <Button variant="secondary" rightIcon={<ChevronRightIcon size={16} data-id="element-1055" />} data-id="element-1054">
                  {isIS ? 'Hafa samband' : 'Get in touch'}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}